import { HorizontalAlignment, StringUtil } from "@mcleod/core";
import { Component, ComponentDeserializer, DeserializeProps, Label, Layout, ResourceFileProps } from "@mcleod/components";
import { LayoutDesignerTab, ResourceDesignerTab, UIDesigner } from "@mcleod/designer";
import { AppLayoutDesignerTab } from "./AppLayoutDesignerTab";
import { AppDesignerMode } from "../AppDesignerMode";

/**
 * AppUIDesigner has two separate modes for designing layouts.
 *
 * The first mode is APP_DESIGN, which functions near identical to standard UIDesigner. The only difference being that
 * when creating a layout specifically for the app, the property `designedForMobile` is set on the layout object's props.
 *
 * The second mode is APP_COMPAT. This mode allows for adjusting components in existing web-centric layouts. It does
 * this by setting a property on any given component called `appProps`, which are a set of override properties for
 * that component. To persist and serialize layouts in app compat mode, a few adjustments have been made here in `AppUIDesigner`
 * and in `AppLayoutDesignerTab`.
 *
 *  - 2 Layout objects.
 *      - The original, `designerPanel`, is responsible for managing user input and displaying UI adjustments accordingly.
 *          - In APP_COMPAT mode, the `designerPanel` is not the object to be serialized. This is the big difference.
 *      - The new layout object, `AppLayoutDesignerTab.compatLayout` is the layout that will be serialized and saved.
 *  - While making adjustments to components via the designer, the changes are saved to `AppLayoutDesignerTab.componentAppPropMap`.
 *      - During the serialization of the `compatLayout` object, the method `AppUIDesigner.applyAppPropsForSerialization` is called
 *          to set the appProps that have been gathered in the map on to the component being serialized.
 */
export class AppUIDesigner extends UIDesigner {
    private _applyAppPropsDuringDeserialization: boolean = true;

    constructor(props?: any) {
        super(props);
        this.setProps({ title: "App UI Designer" });
    }

    override createTab(props: ResourceFileProps): LayoutDesignerTab {
        return new AppLayoutDesignerTab(this, props);
    }

    protected override async deserializeLayout(tab: ResourceDesignerTab, deserializeProps?: Partial<DeserializeProps>) {
        await super.deserializeLayout(tab, deserializeProps);

        this._applyAppPropsDuringDeserialization = false;
        try {
            const props: DeserializeProps = {
                owner: this.createOwnerForPath(tab.path),
                def: tab.definition,
                designer: this, ...deserializeProps
            };
            (tab as AppLayoutDesignerTab).compatLayout =
                await new ComponentDeserializer(props).deserializeSingleComponent() as Layout;
        } finally {
            this._applyAppPropsDuringDeserialization = true;
        }
    }

    override applyChangeToSelectedComponents(data, newValue) {
        const selectedComponents: any = this.selectedComponents;

        super.applyChangeToSelectedComponents(data, newValue);
        if (AppDesignerMode.APP_COMPAT === this.designMode) {
            for (let i = 0; selectedComponents != null && i < selectedComponents.length; i++) {
                const designableObject = selectedComponents[i].designerDataSource ?? selectedComponents[i];
                if (designableObject instanceof Component) {
                    this.saveComponentAppPropsToMap(designableObject, data.prop_name, newValue);
                }
            }
        }
    }

    override doAfterPropChanged(component: Component, propName: string, oldValue: any, newValue: any, redisplayProp?: boolean, propsSeen: string[] = []) {
        super.doAfterPropChanged(component, propName, oldValue, newValue, redisplayProp, propsSeen);
        if (AppDesignerMode.APP_COMPAT === this.designMode)
            this.saveComponentAppPropsToMap(component, propName, newValue);
    }

    private saveComponentAppPropsToMap(component: Component, propName: string, value: any): void {
        if (!component || StringUtil.isEmptyString(component.id))
            return;

        const appProps = this.componentAppPropMap.get(component.id) ?? component.appProps ?? {};
        appProps[propName] = value;
        this.componentAppPropMap.set(component.id, appProps);
    }

    public applyAppPropsForSerialization(component: Component) {
        if (component.id != null && this.designMode == AppDesignerMode.APP_COMPAT) {
            const props = this.componentAppPropMap.get(component.id);
            // if props from map is null, then it was never edited in the designer. So keep existing appProp value.
            if (props != null)
                component.appProps = props;
        }
    }

    public get applyDeserializedAppProps(): boolean {
        return this._applyAppPropsDuringDeserialization;
    }

    override tabChanged(tab: LayoutDesignerTab) {
        super.tabChanged(tab);
        this.manageDesignerMode(tab);
    }

    private manageDesignerMode(tab: LayoutDesignerTab) {
        const compat = AppDesignerMode.APP_COMPAT === this.designMode;
        this.splitterTools.firstComponent = compat ? tab.dataSourcePanel : this.toolsPanel;

        tab.dataSourcePanel.setProps({
            fillHeight: compat,
            fillRow: !compat,
            rowBreak: !compat,
            padding: 0,
            margin: compat ? 0 : 8,
        });
        tab.dataSourcePanel.buttonPanel.visible = !compat;
        tab.dataSourcePanel.targetPanel.setProps({
            fillHeight: compat,
            fillRow: compat,
            rowBreak: compat,
            align: compat ? HorizontalAlignment.CENTER : HorizontalAlignment.LEFT,
            scrollY: true,
            enabled: !compat
        });
        if (compat) {
            if (tab.dataSourcePanel.indexOf(labelDataSources) === -1)
                tab.dataSourcePanel.insert(labelDataSources, 0);
        } else {
            tab.dataSourcePanel.remove(labelDataSources, false);
        }

        tab.dataSourcePanel.targetPanel.components.forEach((c: Component) => c.setProps({ rowBreak: compat }));
    }

    protected override getTestSearchParams(layoutId: string): any {
        return { ...super.getTestSearchParams(layoutId), app: true };
    }

    private get designMode(): AppDesignerMode {
        return (this.getActiveTab() as AppLayoutDesignerTab).getDesignMode();
    }

    private get componentAppPropMap(): Map<string, any> {
        return (this.getActiveTab() as AppLayoutDesignerTab).componentAppPropMap;
    }

    override disablePropertyEditors(prop: any, editorComponents: Component[], selectedComponent: Component): void {
        if (this.designMode === AppDesignerMode.APP_COMPAT && "id" === prop.name) {
            editorComponents.forEach(editor => editor.enabled = false);
        } else {
            super.disablePropertyEditors(prop, editorComponents, selectedComponent);
        }
    }

    override canModifyProp(propName: string, component: Component): boolean {
        if (this.designMode === AppDesignerMode.APP_COMPAT && propName === "id") {
            return false;
        }
        return super.canModifyProp(propName, component);
    }
}

const labelDataSources = new Label({
    id: "labelDataSources",
    text: "Data Sources",
    backgroundColor: "primary",
    align: HorizontalAlignment.CENTER,
    fillRow: true,
    color: "primary.reverse"
});
